import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Input, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge, RenderLocalesWithComponents } from '@/_shared';
import { Routes } from '@/_shared/constants/routes';
import { cutText } from '@/_shared/helpers/cutText';
import { useDebounce } from '@/_shared/hooks/useDebounce';
import { useDialog } from '@/_shared/hooks/useDialog';
import { usePublicDocs } from '@/_shared/hooks/usePublicDocs';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentsCall } from '../../state';
import { Departments } from '../../state/types';

export const DepartmentsList = () => {
  const t = useTranslations('departments.list');
  const navigate = useNavigate();

  const { genericResponsiveValue } = useResponsiveStyle();

  const { departmentsToShow } = getDepartmentsCall.hook();

  const [internalData, setInternalData] = useState<Departments>(departmentsToShow ?? []);

  const debouncedSearch = useDebounce((value: string) => {
    setInternalData(
      (departmentsToShow ?? []).filter(
        department =>
          department &&
          typeof department.name === 'string' &&
          department.name.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  }, 400);

  const goToDetails = (id: string) => {
    navigate(Routes.DepartmentsDetail.replace(':departmentId', id));
  };

  const gapXs = useThemeValue('space', 'xs');

  const { goToPublicsDocs } = usePublicDocs();
  const { openDialog: openContacts } = useDialog('contacts');

  const goToContact = useCallback(() => {
    openContacts({});
  }, [openContacts]);

  return (
    <Box>
      <Box
        display='flex'
        alignItems={genericResponsiveValue({
          breakPoints: { desktop: 'center' },
          defaultValue: 'flex-start',
        })}
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        justifyContent='space-between'
      >
        <Box
          flex={genericResponsiveValue({
            breakPoints: {
              desktop: '2',
            },
            defaultValue: '1',
          })}
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
          width={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: '100%',
          })}
        >
          <Typography variant='h2'>{t('subTitle')}</Typography>
        </Box>
        <Box
          flex='1'
          width={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: '100%',
          })}
        >
          <Input
            suffixSvg='search'
            svgStyle={{
              fill: 'grey/light',
            }}
            inputTextStyle={{ pr: 'l' }}
            type='text'
            placeholder={t('fields.searchPlaceholder')}
            onChange={e => debouncedSearch(e.target.value)}
          />
        </Box>
      </Box>
      <Box mt='l'>
        {internalData && internalData.length === 0 && (
          <>
            <Typography textAlign='center' mt='xxxl' mb='xxl' variant='p2/light'>
              {t('noResults')}
            </Typography>
            <Box width='60%' mx='auto'>
              <AlertBadge
                iconName='info'
                type='transparent'
                label={
                  <RenderLocalesWithComponents
                    text={t('noResultsInfo', {
                      downloadFile: '!{CTA1}!',
                    })}
                    placeholders={{
                      '!{CTA1}!': (
                        <Typography
                          as='span'
                          fontWeight='500'
                          color='primary'
                          onClick={() => goToPublicsDocs({ goTo: 'DEPARTMENTS' })}
                          cursor='pointer'
                        >
                          {t('ctaLabel1')}
                        </Typography>
                      ),
                      '!{CTA2}!': (
                        <Typography
                          as='span'
                          fontWeight='500'
                          color='primary'
                          onClick={() => goToContact()}
                          cursor='pointer'
                        >
                          {t('ctaLabel2')}
                        </Typography>
                      ),
                    }}
                  />
                }
              />
            </Box>
          </>
        )}
        <Box
          style={{ gap: gapXs }}
          display='grid'
          gridTemplateColumns={genericResponsiveValue({
            breakPoints: {
              desktop: 'repeat(3, 1fr)',
              tablet: 'repeat(1, 1fr)',
              mobile: 'repeat(1, 1fr)',
            },
            defaultValue: 'repeat(1, 1fr)',
          })}
        >
          {internalData &&
            internalData.map(department => (
              <Card key={department.id}>
                <Typography variant='h2' color='primary/text'>
                  {department.name}
                </Typography>
                <Box
                  alignItems='center'
                  display='flex'
                  flexDirection='row'
                  justifyContent='space-between'
                  mt='xxxs'
                  borderBottom='1px solid'
                  borderColor='grey/light/background'
                  pb='xs'
                >
                  <Typography variant='p3/label'>
                    {t('team')}
                    <Typography variant='p2/light' as='span'>
                      {cutText(
                        department.teams?.filter(team => team !== 'unknown')?.join(', ') || '-',
                        20,
                      )}
                    </Typography>
                  </Typography>
                  <Box alignItems='center' flexDirection='row' display='flex'>
                    <Svg name='users' size='s' />
                    <Typography ml='xxs' variant='h1'>
                      {department.totalEmployees || 0}
                    </Typography>
                  </Box>
                </Box>
                <Box display='flex' flexDirection='row' justifyContent='space-between' mt='xs'>
                  <Typography variant='p3/label'>{t('manager')}</Typography>
                  <Typography variant='p2/light' as='span'>
                    {`${department?.manager?.firstName ?? ''} ${department?.manager?.lastName ?? ''}`.trim() ||
                      '-'}
                  </Typography>
                </Box>
                <Button
                  onClick={() => department && goToDetails(department?.id || '')}
                  variant='positive'
                  mt='m'
                  fontSize='s'
                  fontWeight='500'
                >
                  {t('viewDetails')}
                </Button>
              </Card>
            ))}
        </Box>
      </Box>
    </Box>
  );
};
